import React, { useState, useEffect, useMemo } from 'react';
import { fetchToxicReport } from '../services/api';
import ReportTable, { BooleanCell, NumberCell, ProductLink } from './ReportTable';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const ToxicReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchToxicReport();
      setData(result);
    } catch (err) {
      setError('Αποτυχία φόρτωσης δεδομένων');
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'productname',
        header: 'Προϊόν',
        cell: ({ row }) => (
          <ProductLink id={row.original.id} name={row.original.productname} />
        ),
      },
      {
        accessorKey: 'qtyinstock',
        header: 'Ποσότητα',
      },
      {
        accessorKey: 'vendorPrice',
        header: 'Τιμή Αγοράς',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'stockPrice',
        header: 'Τιμή Stock',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'daysInStock',
        header: 'Ημέρες στο Stock',
        cell: ({ getValue }) => {
          const days = getValue();
          let colorClass = 'text-gray-900';
          if (days > 730) colorClass = 'text-red-600 font-bold';
          else if (days > 365) colorClass = 'text-orange-600 font-semibold';
          else if (days > 180) colorClass = 'text-yellow-600';
          return <span className={colorClass}>{days}</span>;
        },
      },
      {
        accessorKey: 'eSales60',
        header: 'Πωλήσεις 60 ημ',
      },
      {
        accessorKey: 'toxic',
        header: 'Τοξικό (Τιμή)',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'toxicx2',
        header: 'Τοξικό (Εμπορ.)',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'toxicAuto',
        header: 'Τοξικό Auto',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'toxicx3',
        header: 'Υπερτοξικό',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'category',
        header: 'Κατηγορία',
      },
      {
        accessorKey: 'oldestDate',
        header: 'Ημ/νία Εισαγωγής',
        cell: ({ getValue }) => {
          const date = getValue();
          if (!date) return '-';
          return new Date(date).toLocaleDateString('el-GR');
        },
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Φόρτωση αναφοράς...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="ml-2 text-red-700">{error}</span>
          <button
            onClick={loadData}
            className="ml-4 flex items-center rounded bg-red-100 px-3 py-1 text-sm text-red-700 hover:bg-red-200"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Επανάληψη
          </button>
        </div>
      </div>
    );
  }

  // Calculate summary stats
  const totalValue = data.reduce((sum, item) => sum + (item.vendorPrice * item.qtyinstock), 0);
  const totalItems = data.reduce((sum, item) => sum + item.qtyinstock, 0);
  const over365 = data.filter(item => item.daysInStock > 365).length;
  const over730 = data.filter(item => item.daysInStock > 730).length;

  return (
    <div>
      {/* Summary stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
          <div className="text-sm text-yellow-700">Σύνολο Τοξικών</div>
          <div className="text-2xl font-bold text-yellow-800">{data.length} προϊόντα</div>
        </div>
        <div className="rounded-lg bg-orange-50 border border-orange-200 p-4">
          <div className="text-sm text-orange-700">Συνολική Αξία</div>
          <div className="text-2xl font-bold text-orange-800">
            {new Intl.NumberFormat('el-GR', { minimumFractionDigits: 2 }).format(totalValue)} €
          </div>
        </div>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <div className="text-sm text-red-700">&gt;365 ημέρες</div>
          <div className="text-2xl font-bold text-red-800">{over365} προϊόντα</div>
        </div>
        <div className="rounded-lg bg-red-100 border border-red-300 p-4">
          <div className="text-sm text-red-800">&gt;730 ημέρες</div>
          <div className="text-2xl font-bold text-red-900">{over730} προϊόντα</div>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Σύνολο: {totalItems} τεμάχια
        </div>
        <button
          onClick={loadData}
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Ανανέωση
        </button>
      </div>
      <ReportTable data={data} columns={columns} title="Τοξικά Προϊόντα" />
    </div>
  );
};

export default ToxicReport;
